import { AxiosResponse } from 'axios';
import axiosInstance from 'services/axios/axios';
import { WrapImsPacket } from 'store/utility';

/**
 * Fetch information about staff reporting to a specific user (sparse payload)
 * @param {Number} id T_ESO.FI_ID of the target manager
 * @returns {Promise<AxiosResponse<object[]>>} array of end-user notification objects.
 */
export const fetchBasicStaffList = async id => {
	return axiosInstance
		.get(`/api/personnel/${id}/staff`)
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload;
			}
			return;
		})
		.catch(ex => {
			throw ex;
		});
};

/**
 * Fetch information about staff reporting to a specific user (sparse payload)
 * @param {Number} id T_ESO.FI_ID of the target manager
 * @returns {Promise<AxiosResponse<object[]>>} array of end-user notification objects.
 */
export const fetchBasicDelegatedStaffList = async id => {
	return axiosInstance
		.get(`/api/personnel/${id}/delegatedstaff`)
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload;
			}
			return;
		})
		.catch(ex => {
			throw ex;
		});
};

/**
 * Fetch information about staff reporting to a specific user (verbose payload)
 * @param {Number} id T_ESO.FI_ID of the target manager
 * @returns {Promise<AxiosResponse<object[]>>} array of end-user notification objects.
 */
export const fetchDetailedStaffInformation = async id => {
	return axiosInstance
		.get(`/api/personnel/${id}`, {
			params: {
				verbose: true,
			},
		})
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload;
			}
			return;
		})
		.catch(ex => {
			throw ex;
		});
};

/**
 * Fetch information about target user's manager, manager's manager, and manager's manager's manager (a.k.a., the super-managers)
 * @param {Number} id T_ESO.FI_ID of the target user
 * @returns {Promise<AxiosResponse<object[]>>} array of supervisor going up the organizational chart
 */
export const fetchSuperManagers = async id => {
	return axiosInstance
		.get(`api/personnel/${id}/supermanagers`)
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload;
			}
			return;
		})
		.catch(ex => {
			throw ex;
		});
};

/**
 * Fetch information about target user's account administrator (a.k.a., the account coordinator or site admin)
 * @param {Number} id T_ESO.FI_ID of the target user
 * @returns {Promise<AxiosResponse<object>>} account coordinator details
 */
export const fetchAccountCoordinator = async id => {
	return axiosInstance
		.get(`api/personnel/${id}/accountcoordinator`)
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload;
			}
			return;
		})
		.catch(ex => {
			throw ex;
		});
};

/**
 * TODO
 * @param {string} upn User Principal Name of the target user
 * @returns {Promise<AxiosResponse<object>>} User Account Metadata
 */
export const fetchProxyUser = async id => {
	return await axiosInstance
		.get('api/Identity/LoginIdentity/' + id)
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload;
			}
			return;
		})
		.catch(ex => {
			throw ex;
		});
};

export const submitDelegation = async values => {
	return axiosInstance
		.post('/api/UserAccessReview/v2/Delegation/', WrapImsPacket(values))
		.then(response => {
			return response;
		})
		.catch(error => {
			throw error;
		});
};

export const submitTermination = async values => {
	return axiosInstance
		.post('/api/UserAccessReview/v2/Termination/', WrapImsPacket(values))
		.then(response => {
			return response;
		})
		.catch(error => {
			throw error;
		});
};

export const getUserData = async id => {
	try {
		const response = await axiosInstance.get(
			`/api/UserAccessReview/GetUserInfo/${id}`,
		);
		return response.data.payload;
	} catch (ex) {
		console.error('Unable to fetch: ' + ex);
		return {
			data: [],
			error: 'An Error Occurred Fetching data from the server',
		};
	}
};

export const getPendingRequestSummary = async (requesteeFiId, appId) => {
	const response = await axiosInstance.get(
		'/api/Applications/Requests/PendingStatus',
		{
			params: {
				requesteeFiId,
				appId,
			},
		}
	);

	return response.data?.payload;
};
